export type DocumentUiStatus = 'uploading' | 'upload-interrupted' | 'learning' | 'ready' | 'no-evidence' | 'needs-review' | 'failed' | 'removing' | 'removal-retrying' | 'removal-attention' | 'removed';

export interface DocumentReviewConflictDto {
  field: string;
  evidence_type: string;
  selected_value: string;
  document_value: string;
  source_reference?: { page?: number; page_start?: number; page_end?: number } | null;
}

export interface DocumentReviewDto {
  type: 'MACHINE_ASSOCIATION' | 'CLASSIFICATION' | string;
  status: 'REQUIRED' | 'COMPLETED' | string;
  reason_code: string;
  summary: string;
  selected_machine_name?: string | null;
  assessment?: string | null;
  match_confidence?: number | null;
  identity_strength?: string | null;
  multiple_machines_detected?: boolean;
  conflicts: DocumentReviewConflictDto[];
  legacy_review_context?: boolean;
  available_decisions: Array<'CONFIRM' | 'REJECT'>;
  decision?: string | null;
  reviewed_at?: string | null;
}

export interface DocumentDto {
  document_id: string;
  machine_id: string;
  processing_run_id: string | null;
  original_filename: string;
  file_type: string;
  file_size_bytes: number;
  upload_status: string;
  upload_expires_at?: string | null;
  upload_expired?: boolean;
  abandonment_reason?: string | null;
  processing_status: string;
  security_status: string;
  lifecycle_status?: string;
  version?: number;
  removed_reason?: string | null;
  removed_at?: string | null;
  removed_by?: string | null;
  removal_reconciliation_status?: string | null;
  removal_evidence_revision?: number | null;
  machine_evidence_revision?: number | null;
  machine_evidence_reconciliation_status?: string | null;
  machine_evidence_reconciliation_stage?: string | null;
  machine_evidence_reconciliation_error_code?: string | null;
  machine_evidence_reconciliation_attempt?: number | null;
  machine_evidence_reconciliation_retryable?: boolean | null;
  machine_evidence_reconciliation_failed_at?: string | null;
  machine_evidence_reconciliation_source_document_id?: string | null;
  machine_evidence_reconciliation_retry_requested_at?: string | null;
  created_at?: string | null;
  uploaded_at: string | null;
  original_storage_status: string | null;
  original_location: { bucket: string; key: string } | null;
  content_fingerprint: string | null;
  duplicate_status: string | null;
  duplicate_of_document_id: string | null;
  stored_at: string | null;
  question_ready?: boolean;
  image_evidence_status?: string | null;
  image_evidence_analysis_status?: string | null;
  image_evidence_image_type?: string | null;
  image_evidence_machine_relevance?: string | null;
  image_evidence_generated_at?: string | null;
  image_knowledge_status?: string | null;
  image_knowledge_fact_count?: number;
  image_knowledge_chunk_count?: number;
  image_knowledge_completed_at?: string | null;
  review?: DocumentReviewDto | null;
}

export interface DocumentListResponseDto {
  documents: DocumentDto[];
  request_id?: string;
}

export interface DocumentResponseDto {
  document: DocumentDto;
  request_id?: string;
}

export type ImageEvidenceSupportLevel = 'STRONG' | 'MODERATE' | 'LIMITED' | 'UNKNOWN';

export interface ImageEvidenceObservationDto {
  observation_type: string;
  text: string;
  support_level: ImageEvidenceSupportLevel;
}

export interface ImageEvidenceTextDto {
  text: string;
  context: string;
  support_level: ImageEvidenceSupportLevel;
}

export interface ImageEvidenceDetailDto {
  schema_version: string;
  document_id: string;
  image_id: string;
  analysis_id: string;
  analysis_status: string;
  image_type: string;
  machine_relevance: string;
  summary: string;
  observations: ImageEvidenceObservationDto[];
  extracted_text: ImageEvidenceTextDto[];
  limitations: string[];
  analyzed_at: string;
  text_extraction: { status: string; used: boolean };
  knowledge: {
    status: string | null;
    fact_count: number;
    chunk_count: number;
    completed_at: string | null;
  };
  guidance: string;
}

export interface ImageEvidenceResponseDto {
  image_evidence: ImageEvidenceDetailDto;
  request_id?: string;
}

export interface UploadAuthorizationDto {
  client_file_id: string;
  original_filename?: string;
  document_id?: string;
  upload_token?: string;
  upload_method?: 'PUT';
  upload_url?: string;
  required_headers?: Record<string, string>;
  expires_at?: string;
  status: string;
  error_code?: string;
  message?: string;
  maximum_size_bytes?: number;
}

export interface CreateUploadResponseDto {
  machine_id: string;
  total_files: number;
  accepted_files: number;
  rejected_files: number;
  batch_status: 'ACCEPTED' | 'PARTIAL_SUCCESS' | 'REJECTED';
  uploads: UploadAuthorizationDto[];
  request_id?: string;
}

export interface MachineDocument {
  id: string;
  machineId: string;
  filename: string;
  fileType: string;
  fileSizeBytes: number;
  uploadStatus: string;
  uploadExpiresAt: string | null;
  uploadExpired: boolean;
  abandonmentReason: string | null;
  processingStatus: string;
  securityStatus: string;
  lifecycleStatus: 'ACTIVE' | 'ARCHIVED' | 'REMOVED';
  version: number;
  removedReason: string | null;
  removedAt: string | null;
  removalReconciliationStatus: string | null;
  removalEvidenceRevision: number | null;
  machineEvidenceRevision: number | null;
  machineEvidenceReconciliationStatus: string | null;
  machineEvidenceReconciliationStage: string | null;
  machineEvidenceReconciliationErrorCode: string | null;
  machineEvidenceReconciliationAttempt: number | null;
  machineEvidenceReconciliationRetryable: boolean | null;
  machineEvidenceReconciliationFailedAt: string | null;
  machineEvidenceReconciliationSourceDocumentId: string | null;
  machineEvidenceReconciliationRetryRequestedAt: string | null;
  createdAt: string | null;
  uploadedAt: string | null;
  storedAt: string | null;
  duplicateStatus: string | null;
  duplicateOfDocumentId: string | null;
  questionReady: boolean;
  imageEvidenceStatus: string | null;
  imageEvidenceAnalysisStatus: string | null;
  imageEvidenceImageType: string | null;
  imageEvidenceMachineRelevance: string | null;
  imageEvidenceGeneratedAt: string | null;
  imageKnowledgeStatus: string | null;
  imageKnowledgeFactCount: number;
  imageKnowledgeChunkCount: number;
  imageKnowledgeCompletedAt: string | null;
  review: DocumentReviewDto | null;
  uiStatus: DocumentUiStatus;
  stageLabel: string;
}

const READY_STATUSES = new Set([
  'KNOWLEDGE_CHUNKS_AVAILABLE',
  'READY_FOR_CROSS_DOCUMENT_ANALYSIS',
  'READY_FOR_MACHINE_INSIGHTS',
  'READY_FOR_MACHINE_MEMORY_UPDATE',
  'READY_FOR_PRESENTATION_AND_QUESTIONS',
]);

const REVIEW_STATUSES = new Set([
  'CLASSIFICATION_REVIEW_REQUIRED',
  'MACHINE_ASSOCIATION_REVIEW_REQUIRED',
]);

const FAILED_STATUSES = new Set([
  'BLOCKED',
  'PARSE_FAILED',
  'PROCESSING_UNIT_FAILED',
  'KNOWLEDGE_EXTRACTION_BLOCKED',
]);

const STAGE_LABELS: Record<string, string> = {
  NOT_STARTED: 'Waiting for upload',
  READY_FOR_STORAGE: 'Securing original document',
  STORING_ORIGINAL: 'Securing original document',
  READY_FOR_PARSING: 'Preparing document content',
  READY_FOR_IMAGE_EVIDENCE_ANALYSIS: 'Analyzing visible image evidence',
  PARSING: 'Reading document content',
  RETRY_PENDING: 'Retrying document processing',
  READY_FOR_PROCESSING_UNIT_EVALUATION: 'Preparing document structure',
  CREATING_PROCESSING_UNITS: 'Preparing document structure',
  PROCESSING_UNIT_RETRY_PENDING: 'Retrying document preparation',
  READY_FOR_CLASSIFICATION: 'Understanding document type',
  CLASSIFYING_DOCUMENT: 'Understanding document type',
  CLASSIFICATION_RETRY_PENDING: 'Retrying document understanding',
  CLASSIFICATION_REVIEW_REQUIRED: 'Document type needs review',
  READY_FOR_MACHINE_ASSOCIATION: 'Confirming machine context',
  ASSOCIATING_DOCUMENT_WITH_MACHINE: 'Confirming machine context',
  MACHINE_ASSOCIATION_RETRY_PENDING: 'Retrying machine association',
  MACHINE_ASSOCIATION_REVIEW_REQUIRED: 'Machine association needs review',
  READY_FOR_KNOWLEDGE_EXTRACTION: 'Preparing machine knowledge',
  EXTRACTING_KNOWLEDGE: 'Extracting machine knowledge',
  KNOWLEDGE_EXTRACTION_RETRY_PENDING: 'Retrying knowledge extraction',
  KNOWLEDGE_EXTRACTION_BLOCKED: 'Knowledge extraction blocked',
  READY_FOR_KNOWLEDGE_STORAGE: 'Organizing machine knowledge',
  KNOWLEDGE_CHUNKS_AVAILABLE: 'Ready for grounded questions',
  READY_FOR_CROSS_DOCUMENT_ANALYSIS: 'Ready — broader machine intelligence updating',
  READY_FOR_MACHINE_INSIGHTS: 'Ready — broader machine intelligence updating',
  READY_FOR_MACHINE_MEMORY_UPDATE: 'Ready — Machine Memory updating',
  READY_FOR_PRESENTATION_AND_QUESTIONS: 'Ready',
  PARSE_FAILED: 'Document processing failed',
  PROCESSING_UNIT_FAILED: 'Document preparation failed',
  BLOCKED: 'Document processing blocked',
};

export function deriveDocumentUiStatus(dto: DocumentDto): DocumentUiStatus {
  if (String(dto.lifecycle_status || '').toUpperCase() === 'REMOVED') {
    if (String(dto.removal_reconciliation_status || '').toUpperCase() !== 'PENDING') return 'removed';
    const machineStatus = String(dto.machine_evidence_reconciliation_status || 'PENDING').toUpperCase();
    if (machineStatus === 'ATTENTION_REQUIRED') return 'removal-attention';
    if (machineStatus === 'RETRY_PENDING') return 'removal-retrying';
    return 'removing';
  }
  if (dto.upload_status === 'AWAITING_UPLOAD' && dto.upload_expired) return 'upload-interrupted';
  if (dto.upload_status === 'ABANDONED') return 'upload-interrupted';
  if (['FAILED', 'REJECTED', 'QUARANTINED'].includes(dto.upload_status)) return 'failed';
  if (dto.security_status === 'FAILED' || FAILED_STATUSES.has(dto.processing_status)) return 'failed';
  if (isImageFileType(dto.file_type) && (dto.image_evidence_status === 'REVIEW_REQUIRED' || dto.image_knowledge_status === 'FAILED')) return 'failed';
  if (REVIEW_STATUSES.has(dto.processing_status)) return 'needs-review';
  if (dto.image_knowledge_status === 'NO_ACTIONABLE_EVIDENCE') return 'no-evidence';
  if (dto.question_ready || READY_STATUSES.has(dto.processing_status)) return 'ready';
  if (dto.upload_status === 'AWAITING_UPLOAD') return 'uploading';
  return 'learning';
}

export function documentStageLabel(dto: DocumentDto): string {
  if (String(dto.lifecycle_status || '').toUpperCase() === 'REMOVED') {
    if (String(dto.removal_reconciliation_status || '').toUpperCase() !== 'PENDING') return 'Removed from Machine Memory';
    const machineStatus = String(dto.machine_evidence_reconciliation_status || 'PENDING').toUpperCase();
    if (machineStatus === 'ATTENTION_REQUIRED') return 'Removed — Machine Memory update needs attention';
    if (machineStatus === 'RETRY_PENDING') return 'Removed — Route42 is retrying Machine Memory';
    return 'Removed — Machine Memory updating';
  }
  if ((dto.upload_status === 'AWAITING_UPLOAD' && dto.upload_expired) || dto.upload_status === 'ABANDONED') return 'Upload interrupted';
  if (dto.upload_status === 'AWAITING_UPLOAD') return 'Waiting for browser upload';
  if (dto.upload_status === 'UPLOADED' && dto.processing_status === 'NOT_STARTED') return 'Upload received';
  if (dto.security_status === 'FAILED') return 'Security check failed';
  if (isImageFileType(dto.file_type) && dto.image_evidence_status === 'REVIEW_REQUIRED') return 'Image analysis needs attention';
  if (isImageFileType(dto.file_type) && dto.image_knowledge_status === 'FAILED') return 'Image knowledge processing needs attention';
  if (isImageFileType(dto.file_type) && dto.image_evidence_status === 'RETRY_PENDING') return 'Retrying visible image analysis';
  if (isImageFileType(dto.file_type) && dto.image_knowledge_status === 'RETRY_PENDING') return 'Retrying image knowledge processing';
  if (dto.image_knowledge_status === 'NO_ACTIONABLE_EVIDENCE') return 'Image analyzed — no clear evidence added';
  if (isImageFileType(dto.file_type) && ['READY_FOR_STORAGE', 'STORING_ORIGINAL'].includes(dto.processing_status)) {
    return 'Securing original image';
  }
  return STAGE_LABELS[dto.processing_status] ?? (isImageFileType(dto.file_type) ? 'Route42 is learning from this image' : 'Route42 is learning this document');
}

export function mapMachineDocument(dto: DocumentDto): MachineDocument {
  return {
    id: dto.document_id,
    machineId: dto.machine_id,
    filename: dto.original_filename,
    fileType: dto.file_type,
    fileSizeBytes: dto.file_size_bytes,
    uploadStatus: dto.upload_status,
    uploadExpiresAt: dto.upload_expires_at ?? null,
    uploadExpired: Boolean(dto.upload_expired),
    abandonmentReason: dto.abandonment_reason ?? null,
    processingStatus: dto.processing_status,
    securityStatus: dto.security_status,
    lifecycleStatus: (String(dto.lifecycle_status || 'ACTIVE').toUpperCase() as MachineDocument['lifecycleStatus']),
    version: Number(dto.version ?? 1),
    removedReason: dto.removed_reason ?? null,
    removedAt: dto.removed_at ?? null,
    removalReconciliationStatus: dto.removal_reconciliation_status ?? null,
    removalEvidenceRevision: dto.removal_evidence_revision ?? null,
    machineEvidenceRevision: dto.machine_evidence_revision ?? null,
    machineEvidenceReconciliationStatus: dto.machine_evidence_reconciliation_status ?? null,
    machineEvidenceReconciliationStage: dto.machine_evidence_reconciliation_stage ?? null,
    machineEvidenceReconciliationErrorCode: dto.machine_evidence_reconciliation_error_code ?? null,
    machineEvidenceReconciliationAttempt: dto.machine_evidence_reconciliation_attempt ?? null,
    machineEvidenceReconciliationRetryable: dto.machine_evidence_reconciliation_retryable ?? null,
    machineEvidenceReconciliationFailedAt: dto.machine_evidence_reconciliation_failed_at ?? null,
    machineEvidenceReconciliationSourceDocumentId: dto.machine_evidence_reconciliation_source_document_id ?? null,
    machineEvidenceReconciliationRetryRequestedAt: dto.machine_evidence_reconciliation_retry_requested_at ?? null,
    createdAt: dto.created_at ?? null,
    uploadedAt: dto.uploaded_at,
    storedAt: dto.stored_at,
    duplicateStatus: dto.duplicate_status,
    duplicateOfDocumentId: dto.duplicate_of_document_id,
    questionReady: String(dto.lifecycle_status || 'ACTIVE').toUpperCase() !== 'ACTIVE'
      ? false
      : dto.image_knowledge_status === 'NO_ACTIONABLE_EVIDENCE'
        ? false
        : Boolean(dto.question_ready || READY_STATUSES.has(dto.processing_status)),
    imageEvidenceStatus: dto.image_evidence_status ?? null,
    imageEvidenceAnalysisStatus: dto.image_evidence_analysis_status ?? null,
    imageEvidenceImageType: dto.image_evidence_image_type ?? null,
    imageEvidenceMachineRelevance: dto.image_evidence_machine_relevance ?? null,
    imageEvidenceGeneratedAt: dto.image_evidence_generated_at ?? null,
    imageKnowledgeStatus: dto.image_knowledge_status ?? null,
    imageKnowledgeFactCount: Number(dto.image_knowledge_fact_count ?? 0),
    imageKnowledgeChunkCount: Number(dto.image_knowledge_chunk_count ?? 0),
    imageKnowledgeCompletedAt: dto.image_knowledge_completed_at ?? null,
    review: dto.review ?? null,
    uiStatus: deriveDocumentUiStatus(dto),
    stageLabel: documentStageLabel(dto),
  };
}

export function isDocumentActivelyLearning(document: MachineDocument): boolean {
  if (document.lifecycleStatus === 'REMOVED' && ['removing', 'removal-retrying'].includes(document.uiStatus)) return true;
  if (document.uiStatus === 'learning') return true;
  if (document.uiStatus !== 'uploading') return false;
  const createdAt = document.createdAt ? new Date(document.createdAt).valueOf() : Number.NaN;
  return Number.isNaN(createdAt) || Date.now() - createdAt < 20 * 60 * 1000;
}

export function isImageFileType(fileType: string): boolean {
  return ['png', 'jpg', 'jpeg', 'webp'].includes(fileType.toLocaleLowerCase());
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

export function canonicalContentType(file: File): string {
  const extension = file.name.split('.').pop()?.toLocaleLowerCase() ?? '';
  const types: Record<string, string> = {
    pdf: 'application/pdf',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
  };
  return types[extension] ?? (file.type || 'application/octet-stream');
}
